import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURED_KEYWORDS = ["newlands", "ster-kinekor", "kalk bay", "prize giving", "prize-giving"];

export const HighlightsStrip = () => {
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`, { params: { club: "events" } })
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  const scroll = (dir) =>
    trackRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  const featured = items.filter((i) =>
    FEATURED_KEYWORDS.some((k) => `${i.title} ${i.caption}`.toLowerCase().includes(k))
  );

  if (!featured.length) return null;

  return (
    <section
      className="overflow-hidden bg-gradient-to-br from-ember-subtle via-sand to-teal-subtle pb-14 pt-6"
      data-testid="highlights-section"
    >
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Event highlights</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
              A look back at <span className="text-ember">the year</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/events"
              data-testid="highlights-view-all"
              className="group inline-flex items-center gap-2 rounded-full border border-coal/15 px-5 py-2.5 font-heading text-sm font-bold text-coal transition-colors hover:border-teal hover:text-teal"
            >
              All events
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="highlights-prev"
              onClick={() => scroll(-1)}
              aria-label="Scroll highlights left"
              className="rounded-full border border-coal/10 bg-white p-2.5 text-coal transition-colors hover:border-teal hover:text-teal"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="highlights-next"
              onClick={() => scroll(1)}
              aria-label="Scroll highlights right"
              className="rounded-full border border-coal/10 bg-white p-2.5 text-coal transition-colors hover:border-teal hover:text-teal"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div
          ref={trackRef}
          data-testid="highlights-track"
          className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-2 lg:px-[max(1.5rem,calc((100vw-80rem)/2+1.5rem))]"
        >
          {featured.map((item) => (
            <Link
              key={item.id}
              to="/events"
              data-testid={`highlight-card-${item.id}`}
              className="group relative w-60 shrink-0 snap-start overflow-hidden rounded-3xl shadow-[0_16px_44px_-22px_rgba(26,29,36,0.45)] sm:w-72"
            >
              <img
                src={item.image}
                alt={item.title}
                loading="lazy"
                className={`aspect-[3/4] w-full transition-transform duration-700 group-hover:scale-105 ${
                  item.fit === "contain" ? "bg-[#EDE6DA] object-contain" : "object-cover"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal-dark/85 via-coal-dark/15 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full bg-ember px-3 py-1 font-mono-brand text-[11px] font-bold tracking-widest text-sand">
                {item.year}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-heading text-base font-bold leading-snug text-sand">{item.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-sand/70 line-clamp-2">{item.caption}</p>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
};
